import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { RoutingService } from 'src/app/service/routing.service';
import { ConfigService } from 'src/app/service/config.service';
import { ReportingApiService } from 'src/app/shared/services/reporting-api/reporting-api.service';
import {
  CreateSubscriptionDto,
  ReportSubscription,
  UpdateSubscriptionDto,
  withReportLanguage,
} from 'src/app/shared/utils/reports';

interface ReportSubscriptionResponse {
  hits: ReportSubscription[];
}

const ENDPOINTS = {
  subscriptions: '/v1/subscriptions',
} as const;

@Injectable({
  providedIn: 'root',
})
export class ReportsService {
  private subscriptionsSubject = new BehaviorSubject<ReportSubscription[]>([]);
  public subscriptions$ = this.subscriptionsSubject.asObservable();
  // IDs deleted in this session. Filtered out of every GET response so the
  // server's eventual-consistency / soft-delete behaviour can't resurrect
  // them after a create or refresh.
  private deletedIds = new Set<string>();

  constructor(
    private api: ReportingApiService,
    public routing: RoutingService,
    public config: ConfigService,
  ) {}

  getAllSubscriptions(): Observable<ReportSubscription[]> {
    return this.api
      .get<ReportSubscriptionResponse>(ENDPOINTS.subscriptions)
      .pipe(
        map((response) => {
          const fresh = (response.hits ?? []).filter(
            (sub) => !this.deletedIds.has(sub.id),
          );
          this.subscriptionsSubject.next(fresh);
          return fresh;
        }),
      );
  }

  createSubscription(
    dto: CreateSubscriptionDto,
  ): Observable<ReportSubscription> {
    return this.api.post<ReportSubscription>(
      ENDPOINTS.subscriptions,
      withReportLanguage(dto),
    );
  }

  updateSubscription(
    id: string,
    dto: UpdateSubscriptionDto,
  ): Observable<ReportSubscription> {
    return this.api.put<ReportSubscription>(
      `${ENDPOINTS.subscriptions}/${id}`,
      withReportLanguage(dto),
    );
  }

  /**
   * Create when `id` is absent, update when present. Refreshes the list on success.
   */
  createOrUpdateSubscription(
    payload: CreateSubscriptionDto & { id?: string; active?: boolean },
  ): Observable<ReportSubscription> {
    const { id, ...body } = payload;
    const obs$ = id
      ? this.updateSubscription(id, body)
      : this.createSubscription(body);
    return obs$.pipe(
      map((result) => {
        this.getAllSubscriptions().subscribe();
        return result;
      }),
    );
  }

  deleteSubscription(id: string): Observable<void> {
    return this.api.delete<void>(`${ENDPOINTS.subscriptions}/${id}`).pipe(
      map(() => {
        this.deletedIds.add(id);
        const current = this.subscriptionsSubject.getValue();
        this.subscriptionsSubject.next(current.filter((sub) => sub.id !== id));
      }),
    );
  }
}
